import React, {useState, SyntheticEvent} from 'react';
import {LazyLoadImage} from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';

interface Props {
  src: string;
  width?: number | string;
  height?: number | string;
  style?: React.CSSProperties;
  onLoad?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  onError?: (event: SyntheticEvent<HTMLImageElement, Event>) => void;
  priority?: boolean; // 우선순위가 높은 이미지인 경우
  className?: string;
  wrapperClassName?: string;
  wrapperProps?: {
    style?: React.CSSProperties;
  };
}

export default function Image({
  src,
  width,
  height,
  style,
  onLoad,
  onError,
  priority = false,
  className,
  wrapperClassName,
  wrapperProps,
}: Props) {
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRY = 2;

  const handleError = () => {
    if (retryCount < MAX_RETRY) {
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        const tempImg = new window.Image();
        tempImg.src = src;
        tempImg.onload = () =>
          onLoad?.(
            new Event('load') as unknown as SyntheticEvent<
              HTMLImageElement,
              Event
            >,
          );
        tempImg.onerror = () =>
          onError?.(
            new Event('error') as unknown as SyntheticEvent<
              HTMLImageElement,
              Event
            >,
          );
      }, 1000);
    } else {
      onError?.(
        new Event('error') as unknown as SyntheticEvent<
          HTMLImageElement,
          Event
        >,
      );
    }
  };

  return (
    <span className={wrapperClassName} style={wrapperProps?.style}>
      <LazyLoadImage
        src={src}
        alt="example"
        effect="blur"
        width={`${width || 'auto'}`}
        height={`${height || 'auto'}`}
        style={style}
        className={className}
        wrapperClassName="lazy-load-image-wrapper"
        wrapperProps={{
          style: {
            width: '100%',
            height: '100%',
            display: 'block',
            position: 'relative',
          },
        }}
        onLoad={onLoad}
        onError={handleError}
        threshold={priority ? 0 : 100} // 우선순위 이미지는 즉시 로드 시작
        // 최소한의 지연 설정으로 스크롤시에도 빠르게 로드
        placeholderSrc={src.replace(/\.[^.]+$/, '_thumb.jpg')}
        visibleByDefault={priority} // 우선순위 이미지는 즉시 표시
      />
    </span>
  );
}
